import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnDestroy, OnInit, Output, ChangeDetectorRef } from '@angular/core';
import { Select, Store } from '@ngxs/store';
import { combineLatest, Observable, Subscription, map, BehaviorSubject } from 'rxjs';
import { SetChallengeDetails, SetChallenges } from '../../../../store/actions/user.actions';
import { NavigationService } from '@modules/navigation/services';
import { VendorSetup } from '../../../../vendors/directus/interfaces/vendor.interface';
import { Order } from '../../../../interfaces/order.interface';
import { User } from '../../../../interfaces/user.interface';
import { LoyaltySection } from '../../../../vendors/directus/interfaces/main-settings.interface';
import { GlobalStateModel } from '../../../../store/state.model';
import { ChallengeDetail } from '../../../../interfaces/challenge-details.interface';
import { Challenge } from '../../../../interfaces/challenges.interface';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ChallengeDetailModalComponent } from '../../../loyalty/components';

@Component({
  selector: 'app-challenges-section',
  templateUrl: './challenges-section.component.html',
  styleUrls: ['challenges-section.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ChallengesSectionComponent implements OnInit, OnDestroy {
  @Select(state => state.app.vendorSetup) vendorSetup$: Observable<VendorSetup>;
  @Select(state => state.order.order) order$: Observable<Order>;
  @Select(state => state.user.user) user$: Observable<User>;
  @Select((state: GlobalStateModel) => state.user.challenges) challenges$: Observable<Challenge[]>;
  @Select((state: GlobalStateModel) => state.user.challengeDetails) challengeDetailsMap$: Observable<{
    [challengeId: number]: ChallengeDetail;
  }>;

  @Input() section: LoyaltySection;
  @Input() challenges: Challenge[] = [];
  @Input() isDashboard = false;

  @Output() orderNow = new EventEmitter<void>();

  // Display variables for view
  displayVendorSetup: VendorSetup;
  displayOrder: Order;
  displayUser: User;
  isLoading$ = new BehaviorSubject<boolean>(false);
  locationsRoute = this.navigation.getLocationsPageSlug();
  challengesRoute = '/loyalty/challenges';
  private subs: Subscription[] = [];

  // Combined challenges observable - will be initialized in constructor
  combinedChallenges$: Observable<Array<Challenge & { details: ChallengeDetail | null }>>;

  constructor(
    private store: Store,
    private navigation: NavigationService,
    private modal: NgbModal,
    private cdr: ChangeDetectorRef
  ) {
    // Initialize combinedChallenges$ in constructor to avoid initialization order issues
    this.combinedChallenges$ = combineLatest([this.challenges$, this.challengeDetailsMap$]).pipe(
      map(([challenges, detailsMap]) => {
        if (challenges) {
          const combined = challenges.map(challenge => ({
            ...challenge,
            details: detailsMap ? detailsMap[challenge.id] : null,
          }));
          
          // Log past_completions for each challenge
          combined.forEach(challenge => {
            if (challenge.details) {
              console.log(`Challenge ${challenge.id} past_completions:`, challenge.details.past_completions);
            }
          });
          
          return combined;
        }
        return [];
      })
    );
  }

  ngOnInit() {
    this.isLoading$.next(true);

    // Initialize challenges data
    this.store
      .selectOnce((state: GlobalStateModel) => state.user.user)
      .subscribe(user => {
        if (user && user.userID) {
          // Dispatch action to fetch challenges using the userID
          this.store.dispatch(new SetChallenges(user.userID)).subscribe({
            next: () => {
              // Once challenges are loaded, select the challenges from the store
              this.store
                .selectOnce((state: GlobalStateModel) => state.user.challenges)
                .subscribe({
                  next: challenges => {
                    // Dispatch the challenge details action for each challenge with the userID and challenge id
                    if (challenges && challenges.length > 0) {
                      challenges.forEach(challenge => {
                        this.store.dispatch(new SetChallengeDetails(user.userID, challenge.id));
                      });
                    }
                    this.isLoading$.next(false);
                    this.cdr.markForCheck();
                  },
                  error: error => {
                    console.error('Error loading challenges:', error);
                    this.isLoading$.next(false);
                    this.cdr.markForCheck();
                  },
                });
            },
            error: error => {
              console.error('Error dispatching SetChallenges:', error);
              this.isLoading$.next(false);
              this.cdr.markForCheck();
            },
          });
        } else {
          this.isLoading$.next(false);
          this.cdr.markForCheck();
        }
      });

    // Subscribe to vendor setup
    this.subs.push(
      this.vendorSetup$.subscribe(vendorSetup => {
        if (vendorSetup) {
          this.displayVendorSetup = vendorSetup;
          this.cdr.markForCheck();
        }
      })
    );

    this.subs.push(
      this.order$.subscribe(order => {
        if (order) {
          this.displayOrder = order;
          this.cdr.markForCheck();
        }
      })
    );

    this.subs.push(
      this.user$.subscribe(user => {
        if (user) {
          this.displayUser = user;
        } else {
          this.displayUser = null;
        }
        this.cdr.markForCheck();
      })
    );
  }

  ngOnDestroy() {
    this.subs.forEach(sub => sub.unsubscribe());
  }

  openChallengeDetailModal(challengeDetail: ChallengeDetail, challenge?: Challenge) {
    if (!challengeDetail && challenge) {
      // If challenge details are not loaded, try to load them first
      this.store
        .selectOnce((state: GlobalStateModel) => state.user.user)
        .subscribe(user => {
          if (user && user.userID) {
            this.store.dispatch(new SetChallengeDetails(user.userID, challenge.id)).subscribe({
              next: () => {
                // Wait a moment for the state to update, then try to open the modal again
                setTimeout(() => {
                  this.store
                    .selectOnce((state: GlobalStateModel) => state.user.challengeDetails)
                    .subscribe(detailsMap => {
                      const loadedDetails = detailsMap ? detailsMap[challenge.id] : null;
                      if (loadedDetails) {
                        console.log('Loaded challenge details past_completions:', loadedDetails.past_completions);
                        this.openChallengeDetailModal(loadedDetails);
                      } else {
                        console.warn('Failed to load challenge details for challenge:', challenge.id);
                      }
                    });
                }, 100);
              },
              error: error => {
                console.error('Error loading challenge details:', error);
              },
            });
          }
        });
      return;
    }

    if (!challengeDetail) {
      console.warn('No challenge details available for modal');
      return;
    }

    console.log('Opening modal with challenge details past_completions:', challengeDetail.past_completions);

    try {
      const modalRef = this.modal.open(ChallengeDetailModalComponent, {
        modalDialogClass: 'challenge-detail-modal condensed-modal',
        centered: true,
      });

      if (modalRef && modalRef.componentInstance) {
        modalRef.componentInstance.challengeDetail = challengeDetail;
      } else {
        console.error('Failed to open challenge detail modal');
      }
    } catch (error) {
      console.error('Error opening challenge detail modal:', error);
    }
  }

  routeToProfile() {
    this.navigation.navigateToProfilePage();
  }
}
